import FilteredSelectField from "../filtered-select-field";
import { FunctionComponent, memo, useMemo } from "react";
import { IProps } from "./select-tag.types";
import { ITag } from "@edgetier/watchtower-types";
import useTags from "hooks-for/tags/use-tags";
import { groupBy } from "@edgetier/utilities";
import "./select-tag.scss";

/**
 * Select one or more tags for filtering. The tags are cached for a long time as they should change rarely.
 * @param props.name Field name.
 * @returns          Tag filter.
 */
const SelectTag: FunctionComponent<IProps> = ({ name, tagGroupId, parameters, ...selectProps }) => {
    const { data: allTags } = useTags({ tagGroupId });

    // These tags are in the URL and the user expects to see them in the form. They must be included in the select.
    const selectedTags = useMemo(
        () => (allTags ?? []).filter((tag) => (parameters?.tagId ?? []).includes(tag.tagId)),
        [allTags, parameters?.tagId]
    );

    // These are the tags that have been filtered by the parameters in the URL and the values in the form.
    const { data: filteredTags, isLoading } = useTags(
        { ...parameters, tagGroupId },
        {
            enabled: typeof parameters !== "undefined",
            select: (filteredTags) => {
                // Create object with tag IDs as keys so no iteration over an array is needed inside the filter.
                const groupedTags = groupBy(filteredTags, ({ tagId }) => tagId);
                // Add selectedTags if they are not returned from the filtered tags request.
                const tagsToAdd = selectedTags?.filter(({ tagId }) => tagId in groupedTags === false);

                return [...filteredTags, ...(tagsToAdd ?? [])];
            },
        }
    );

    return (
        <FilteredSelectField<ITag>
            getLabel={({ tag }) => tag}
            getValue={({ tagId }) => tagId}
            isLoading={isLoading}
            allOptions={allTags ?? []}
            filteredOptions={filteredTags}
            name={name}
            {...selectProps}
        />
    );
};

export default memo(SelectTag);
